import { NavLink, useLocation } from "react-router-dom";
import { LayoutGrid, Users, UserPlus, User as UserIcon, LogOut, Building2, BarChart3, Target, CalendarDays, MoreHorizontal } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/",              label: "Dashboard",     icon: LayoutGrid,   testid: "nav-dashboard",     adminOnly: false, short: "Home" },
  { to: "/leads",         label: "Leads",         icon: Users,        testid: "nav-leads",         adminOnly: false },
  { to: "/opportunities", label: "Opportunities", icon: Target,       testid: "nav-opportunities", adminOnly: false, short: "Opps" },
  { to: "/tasks",         label: "Tasks",         icon: CalendarDays, testid: "nav-tasks",         adminOnly: false },
  { to: "/projects",      label: "Projects",      icon: Building2,    testid: "nav-projects",      adminOnly: false, more: true },
  { to: "/reports",       label: "Reports",       icon: BarChart3,    testid: "nav-reports",       adminOnly: true,  more: true },
  { to: "/team",          label: "Team",          icon: UserPlus,     testid: "nav-team",          adminOnly: true,  more: true },
  { to: "/profile",       label: "Profile",       icon: UserIcon,     testid: "nav-profile",       adminOnly: false, more: true },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const nav = NAV.filter((n) => !n.adminOnly || user?.role === "admin");
  const pageTitle =
    nav.find((n) => (n.to === "/" ? location.pathname === "/" : location.pathname.startsWith(n.to)))?.label ||
    "Dashboard";

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop side nav */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-slate-100 bg-white lg:flex">
        <div className="flex items-center gap-3 px-6 py-6">
          <Logo className="h-10 w-10" />
          <div>
            <div className="text-[15px] font-bold leading-none text-navy">IPC CRM</div>
            <div className="mt-1 text-[11px] uppercase tracking-wider text-slate-400">
              Property Consultant
            </div>
          </div>
        </div>
        <nav className="mt-2 flex flex-col gap-1 px-3">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`side-${n.testid}`}
              end={n.to === "/"}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition ${
                  isActive
                    ? "bg-[hsl(var(--brand-navy))] text-white shadow-sm"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto px-4 pb-6">
          <button
            data-testid="side-logout-btn"
            onClick={logout}
            className="flex w-full items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-20 border-b border-slate-100 bg-white/90 backdrop-blur">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 lg:px-8">
            <div className="flex items-center gap-3 lg:hidden">
              <Logo className="h-8 w-8" />
              <div>
                <div className="text-[13px] font-semibold text-navy leading-none">Hello, {user?.name?.split(" ")[0] || "there"} <span className="ml-1">👋</span></div>
                <div className="mt-1 text-[11px] text-slate-500 capitalize">{user?.role || ""}</div>
              </div>
            </div>
            <h1 className="hidden text-xl font-bold text-navy lg:block">{pageTitle}</h1>
            <div className="flex items-center gap-2">
              <NotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="topbar-avatar" className="outline-none">
                    <Avatar className="h-9 w-9 bg-[hsl(var(--brand-navy))] text-white">
                      <AvatarFallback className="bg-[hsl(var(--brand-navy))] text-sm font-semibold text-white">
                        {user?.initials || (user?.name?.[0] ?? "U")}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="text-sm font-semibold">{user?.name}</div>
                    <div className="text-xs text-slate-500">{user?.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={logout}>
                    <LogOut className="h-4 w-4" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="mx-auto max-w-6xl px-4 pb-24 pt-4 lg:px-8 lg:pb-10 lg:pt-6">
          {children}
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav
        className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-100 bg-white/95 backdrop-blur lg:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="mx-auto flex max-w-lg">
          {nav.filter((n) => !n.more).map((n) => {
            const active = n.to === "/" ? location.pathname === "/" : location.pathname.startsWith(n.to);
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`bottom-${n.testid}`}
                data-active={active}
                className="nav-item"
                end={n.to === "/"}
              >
                <n.icon className="h-5 w-5" />
                <span>{n.short || n.label}</span>
              </NavLink>
            );
          })}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="bottom-nav-more"
                data-active={nav.some((n) => n.more && location.pathname.startsWith(n.to))}
                className="nav-item"
              >
                <MoreHorizontal className="h-5 w-5" />
                <span>More</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="mb-2 w-48">
              {nav.filter((n) => n.more).map((n) => (
                <DropdownMenuItem key={n.to} asChild>
                  <NavLink to={n.to} data-testid={`bottom-${n.testid}`} className="flex items-center gap-2">
                    <n.icon className="h-4 w-4" /> {n.label}
                  </NavLink>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>
    </div>
  );
}
